import { Permission } from "./../plugins/fake-api/handlers/apps/permission/types";
// src/api/types.ts
export type ApiResponse<T> = {
  data: T;
  message?: string;
  success?: boolean;
};

export interface ApiErrorData {
  status?: number;
  message?: string;
  code?: string;
  /**
   * The server considered this request and REFUSED it — a validation failure, a state rule, a
   * permission rule. Distinct from a request that never got an answer.
   *
   * Two things read it: `retry()` (a refusal is deterministic, so re-sending it only repeats
   * the same failure and makes it look intermittent) and the transport branch of the response
   * interceptor (which must not relabel a refusal as a network problem).
   */
  isRefusal?: boolean;
  /** Diagnostic payload — console and network tab only. May contain a server traceback. */
  details?: any;
}

export class ApiError extends Error {
  public status?: number;
  public data?: ApiErrorData;
  constructor(message: string, status?: number, data?: ApiErrorData) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/** True when the backend answered and said no — see `ApiErrorData.isRefusal`. */
export function isApiRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.data?.isRefusal === true;
}
export enum FrappeMethodType {
  resource = "resource",
  method = "method",
}
export enum Controllers {
  role = "Role",
  Permission = "Role%20Profile",
  patient = "Patient Encounter",
  guardian = "Guardian",
  visit = "Visit",
  user = "User",
  pet = "Pet",
  petBreed = "Pet%20Breed",
  petMedicalProfile = "Pet%20Medical%20Profile",
  customer = "Customer",
  product = "Product",
  store = "store",
  petGuardian = "PetGuardian",
  foodType = "FoodType",
  foodBrand = "Brand",
  careServices = "CareService%20template",
  careServiceBillingOption = "Care%20Service%20Billing%20Option",
  categoryCareServices = "CategoryCareServices",
  petCareService = "PetCareService",
  lab = "Lab",
  radiology = "Radiology",
  imaging = "Imaging",
  petProcedure = "Pet%20Procedure",
  serviceRoom = "Service%20Room",
  medication = "Medication",
  doctor = "Healthcare%20Practitioner",
  procedureTemplate = "Procedure%20Template",
  appointment = "Appointment",
  vetCaseSheet = "Vet%20Case%20Sheet",
  vetAppointment = "Vet%20Appointment",
  vetVisit = "Vet%20Visit",
  petBoarding = "Pet%20Boarding",
  order = "Sales%20Order",
  categoryItemEcommerce = "Product%20Category",
  dashboard = "pet_app.api.dashboard.get_order_status_counts",
  driver = "Driver",
  deliveryPartner = "Delivery%20Partner",
  deliveryPartnerSettlement = "Delivery%20Partner%20Settlement",
  address = "Address",
  supplier = "Supplier",
  purchaseInvoice = "Purchase%20Invoice",
  salesInvoice = "Sales%20Invoice",
  paymentEntry = "Payment%20Entry",
  journalEntry = "Journal%20Entry",
  posProfile = "POS%20Profile",
  modeOfPayment = "Mode%20of%20Payment",
  account = "Account",
  activityLog = "Activity%20Log",
  cashierSettlement = "Pet%20App%20Cashier%20Settlement",
  coupon = "Coupon%20Code",
  mobileHomeBanner = "Mobile%20Home%20Banner",
  mobileHomeCollection = "Mobile%20Home%20Product%20Collection",
  mobileHomeFilter = "Mobile%20Home%20Filter",
  report = "Report",
  whatsappAccount = "Pet%20App%20WhatsApp%20Account",
  whatsappTemplate = "Pet%20App%20WhatsApp%20Template",
  notificationRule = "Pet%20App%20Notification%20Rule",
  communicationConsent = "Pet%20App%20Communication%20Consent",
  pushNotificationSettings = "Pet%20App%20Notification%20Settings",
}

export enum HttpClientMethods {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

export enum Permissions {
  Driver = "Driver",
  Visit = "Visit",
  Setting = "Setting",
  Guardians = "Guardians",
  Users = "Users",
  Order = "Order",
  Ecommerce = "E-commerce",
  Pet = "Pet",
  PetAppAdmin = "Pet App Admin",
  LabTestApprover = "LabTest Approver",
  Patient = "Patient",
  LaboratoryUser = "Laboratory User",
  Physician = "Physician",
  NursingUser = "Nursing User",
  Coordinator = "Coordinator",
  HealthcareCoordinator = "Healthcare Coordinator",
  HealthcareAdministrator = "Healthcare Administrator",
  ClinicReception = "Clinic Reception",
  Guardian = "Guardian",
  Analytics = "Analytics",
  Supplier = "Supplier",
  SupportTeam = "Support Team",
  FulfillmentUser = "Fulfillment User",
  QualityManager = "Quality Manager",
  DeliveryUser = "Delivery User",
  DeliveryManager = "Delivery Manager",
  FleetManager = "Fleet Manager",
  AcademicsUser = "Academics User",
  Customer = "Customer",
  ItemManager = "Item Manager",
  ManufacturingUser = "Manufacturing User",
  HRUser = "HR User",
  ProjectsUser = "Projects User",
  ProjectsManager = "Projects Manager",
  ManufacturingManager = "Manufacturing Manager",
  HRManager = "HR Manager",
  StockManager = "Stock Manager",
  StockUser = "Stock User",
  Employee = "Employee",
  Auditor = "Auditor",
  MaintenanceManager = "Maintenance Manager",
  SalesManager = "Sales Manager",
  PurchaseManager = "Purchase Manager",
  SalesMasterManager = "Sales Master Manager",
  MaintenanceUser = "Maintenance User",
  PurchaseMasterManager = "Purchase Master Manager",
  AccountsManager = "Accounts Manager",
  PurchaseUser = "Purchase User",
  SalesUser = "Sales User",
  AccountsUser = "Accounts User",
  KnowledgeBaseEditor = "Knowledge Base Editor",
  KnowledgeBaseContributor = "Knowledge Base Contributor",
  MarketingManager = "Marketing Manager",
  NewsletterManager = "Newsletter Manager",
  PreparedReportUser = "Prepared Report User",
  InboxUser = "Inbox User",
  Translator = "Translator",
  ScriptManager = "Script Manager",
  ReportManager = "Report Manager",
  WorkspaceManager = "Workspace Manager",
  DashboardManager = "Dashboard Manager",
  WebsiteManager = "Website Manager",
  Administrator = "Administrator",
  DeskUser = "Desk User",
  All = "All",
  SystemManager = "System Manager",
  Guest = "Guest",
  GuardianPhoneHidden = "Guardian Phone Hidden",
  StaffPhoneHidden = "Staff Phone Hidden",
  CrmPhoneHidden = "CRM Phone Hidden",
  /** POS: see the delivery cards (driver orders, van load). Exact backend Role name. */
  PosDriverOrders = "POS_DRIVERES_orders",
  /** POS: record delivery results and settle drivers. Implies `PosDriverOrders`. */
  PosDriverOrdersResult = "POS_DRIVERES_orders_result",
}
